import { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { simulationsAPI, catalogAPI, patientAPI, protocolsAPI } from '../api';
import { useAuth } from './AuthContext';

const SimulationContext = createContext(null);

// Clinical role definitions
export const CLINICAL_ROLES = {
  medico: {
    id: 'medico',
    name: 'Médico',
    icon: 'Stethoscope',
    color: 'blue',
    description: 'Diagnóstico, indicación de tratamiento, solicitud de exámenes, procedimientos avanzados'
  },
  enfermera: {
    id: 'enfermera',
    name: 'Enfermera',
    icon: 'FirstAidKit',
    color: 'green',
    description: 'Administración de medicamentos, monitorización, preparación de insumos'
  },
  tens: {
    id: 'tens',
    name: 'TENS',
    icon: 'Heartbeat',
    color: 'purple',
    description: 'Control de signos vitales, apoyo en procedimientos, ejecución de tareas básicas'
  },
  paramedico: {
    id: 'paramedico',
    name: 'Paramédico',
    icon: 'Ambulance',
    color: 'orange',
    description: 'Atención prehospitalaria, RCP, desfibrilación, estabilización'
  },
  matrona: {
    id: 'matrona',
    name: 'Matrona',
    icon: 'Baby',
    color: 'pink',
    description: 'Atención de parto, monitorización fetal, procedimientos obstétricos'
  }
};

export const SimulationProvider = ({ children }) => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  
  const [session, setSession] = useState(null);
  const [scenario, setScenario] = useState(null);
  const [vitals, setVitals] = useState({
    fc: 80,
    spo2: 98,
    pa_sistolica: 120,
    pa_diastolica: 80,
    fr: 16,
    temperatura: 36.5,
    ritmo_ecg: null
  });
  const [patientState, setPatientState] = useState('estable');
  // v10.3.1 Fase 2: transición progresiva de vitales enviada por el
  // instructor. Cuando != null, el componente que consume `vitals` debe
  // usar `useVitalsTransition` para interpolar suavemente.
  const [activeVitalsTransition, setActiveVitalsTransition] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [medications, setMedications] = useState([]);
  const [actions, setActions] = useState([]);
  const [medicationsCatalog, setMedicationsCatalog] = useState([]);
  const [actionsCatalog, setActionsCatalog] = useState([]);
  const [labTestsCatalog, setLabTestsCatalog] = useState([]);
  const [imagingCatalog, setImagingCatalog] = useState([]);
  const [catalogsLoaded, setCatalogsLoaded] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [debriefing, setDebriefing] = useState(null);
  const [isTimedOut, setIsTimedOut] = useState(false);
  const [instructorPaused, setInstructorPaused] = useState(false);
  const [extraTime, setExtraTime] = useState(0);
  const [simulationPhase, setSimulationPhase] = useState('briefing'); // briefing, anamnesis, examen, tratamiento
  
  // Clinical role and protocol state
  const [clinicalRole, setClinicalRole] = useState('medico');
  const [selectedProtocol, setSelectedProtocol] = useState(null);
  const [availableProtocols, setAvailableProtocols] = useState([]);
  const [protocolMedications, setProtocolMedications] = useState([]);
  const [protocolActions, setProtocolActions] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [teamNotifications, setTeamNotifications] = useState([]);
  
  // Scenario-specific medications and actions (filtered by role and stage)
  const [scenarioMedications, setScenarioMedications] = useState([]);
  const [scenarioActions, setScenarioActions] = useState([]);
  const [scenarioClinicalConfig, setScenarioClinicalConfig] = useState(null);
  const [useScenarioFiltering, setUseScenarioFiltering] = useState(true); // Enable filtered mode
  
  // Patient interaction data
  const [anamnesisHistory, setAnamnesisHistory] = useState([]);
  const [physicalExamResults, setPhysicalExamResults] = useState([]);
  const [labResults, setLabResults] = useState([]);
  const [imagingResults, setImagingResults] = useState([]);
  
  // Deterioration and death state
  const [isDeceased, setIsDeceased] = useState(false);
  const [causeOfDeath, setCauseOfDeath] = useState(null);
  const [deteriorationAlerts, setDeteriorationAlerts] = useState([]);
  const [eventAlerts, setEventAlerts] = useState([]); // Eventos condicionales del escenario (IAM Demo)
  const [timeInParo, setTimeInParo] = useState(0);
  const [timeInCritical, setTimeInCritical] = useState(0);
  const [criticalOmissions, setCriticalOmissions] = useState([]);
  
  // Recovery state
  const [recoveryAlert, setRecoveryAlert] = useState(null);
  const [isRecovering, setIsRecovering] = useState(false);
  
  const timerRef = useRef(null);
  const audioContextRef = useRef(null);
  const lastActionTimeRef = useRef(0);  // Track when last action was performed
  const evolutionIntervalRef = useRef(null);  // Reference for evolution interval
  const localVitalsRef = useRef(null);  // Keep track of latest local vitals to prevent overwrites
  const instructorOverrideAtRef = useRef(0);  // Track last instructor WS override (ms since epoch)
  const addTimelineEventRef = useRef(null);  // Ref for addTimelineEvent to avoid circular dependency

  // Load catalogs only when authenticated
  useEffect(() => {
    const loadCatalogs = async () => {
      try {
        const [medsRes, actionsRes, labsRes, imagingRes] = await Promise.all([
          catalogAPI.getMedications(),
          catalogAPI.getClinicalActions(),
          catalogAPI.getLabTests(),
          catalogAPI.getImagingStudies()
        ]);
        setMedicationsCatalog(medsRes.data);
        setActionsCatalog(actionsRes.data);
        setLabTestsCatalog(labsRes.data);
        setImagingCatalog(imagingRes.data);
        setCatalogsLoaded(true);
      } catch (error) {
        console.error('Error loading catalogs:', error);
      }
    };
    
    // Only load catalogs when user is authenticated and auth is not loading
    if (isAuthenticated && !authLoading && !catalogsLoaded) {
      loadCatalogs();
    }
    
    // Reset catalogs when user logs out
    if (!isAuthenticated && !authLoading) {
      setMedicationsCatalog([]);
      setActionsCatalog([]);
      setLabTestsCatalog([]);
      setImagingCatalog([]);
      setCatalogsLoaded(false);
    }
  }, [isAuthenticated, authLoading, catalogsLoaded]);

  // Timer logic
  useEffect(() => {
    if (isRunning && !instructorPaused) {
      timerRef.current = setInterval(() => {
        setElapsedTime(prev => prev + 1);
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isRunning, instructorPaused]);

  // Fase C: timeout de servidor — bloquear la UI al agotar el tiempo máximo del escenario
  useEffect(() => {
    if (!isRunning || isTimedOut || instructorPaused) return;
    const maxT = scenario?.tiempo_maximo ? scenario.tiempo_maximo + extraTime : null;
    if (maxT && elapsedTime >= maxT) {
      setIsRunning(false);
      setIsTimedOut(true);
    }
  }, [elapsedTime, isRunning, isTimedOut, scenario, extraTime, instructorPaused]);

  // Automatic patient evolution - only after 20 seconds of inactivity
  useEffect(() => {
    // Si el paciente ya falleció, no evolucionar más
    if (isDeceased) {
      if (evolutionIntervalRef.current) {
        clearInterval(evolutionIntervalRef.current);
        evolutionIntervalRef.current = null;
      }
      return;
    }
    
    if (!isRunning || !session?.id) {
      // Clean up interval when not running
      if (evolutionIntervalRef.current) {
        clearInterval(evolutionIntervalRef.current);
        evolutionIntervalRef.current = null;
      }
      return;
    }

    const INACTIVITY_THRESHOLD = 20000; // 20 seconds of inactivity before evolution
    const INSTRUCTOR_OVERRIDE_WINDOW = 5 * 60 * 1000; // 5 min window: engine won't overwrite instructor's changes

    const evolvePatient = async () => {
      const timeSinceLastAction = Date.now() - lastActionTimeRef.current;

      // Only evolve if no action was performed in the last 20 seconds
      if (timeSinceLastAction < INACTIVITY_THRESHOLD) {
        return; // User is still active, skip evolution
      }

      // Instructor Override precedence (spec §5): if an instructor has
      // modified vitals/state via WebSocket recently, the scenario engine
      // must NOT overwrite those values with its own (possibly initial)
      // state. Otherwise the student's monitor snaps back to asistolia.
      const overrideAt = instructorOverrideAtRef.current || 0;
      if (Date.now() - overrideAt < INSTRUCTOR_OVERRIDE_WINDOW) {
        return; // Instructor is driving the simulation — hands off.
      }
      
      try {
        // Update time on server first
        await simulationsAPI.updateState(session.id, { tiempo_transcurrido: elapsedTime });
        
        // Get evolved state from engine
        const response = await simulationsAPI.evolve(session.id);
        if (response.data) {
          // Only update if there hasn't been a recent action (double-check)
          const currentTimeSinceAction = Date.now() - lastActionTimeRef.current;
          if (currentTimeSinceAction >= INACTIVITY_THRESHOLD) {
            setVitals(response.data.vitals);
            setPatientState(response.data.patient_state);
            // Update local reference
            localVitalsRef.current = response.data.vitals;
            
            // Manejar deterioro y alertas
            if (response.data.deterioration_alerts && response.data.deterioration_alerts.length > 0) {
              setDeteriorationAlerts(response.data.deterioration_alerts);
            }
            
            // Manejar alertas de eventos condicionales (IAM Demo y otros escenarios con condiciones)
            if (response.data.event_alerts && response.data.event_alerts.length > 0) {
              setEventAlerts(prev => [...prev, ...response.data.event_alerts]);
              // Auto-limpiar alertas después de 10 segundos
              setTimeout(() => {
                setEventAlerts(prev => prev.filter(a => 
                  Date.now() - (a.displayTime || 0) < 10000
                ));
              }, 10000);
            }
            
            // Manejar tiempos críticos
            if (response.data.time_in_paro !== undefined) {
              setTimeInParo(response.data.time_in_paro);
            }
            if (response.data.time_in_critical !== undefined) {
              setTimeInCritical(response.data.time_in_critical);
            }
            if (response.data.critical_omissions) {
              setCriticalOmissions(response.data.critical_omissions);
            }
            
            // Manejar muerte del paciente
            if (response.data.is_deceased) {
              setIsDeceased(true);
              setCauseOfDeath(response.data.cause_of_death);
              setPatientState('fallecido');
              
              // Detener la simulación
              setIsRunning(false);
              if (timerRef.current) {
                clearInterval(timerRef.current);
              }
              
              // Agregar evento de muerte al timeline local (using ref to avoid circular dependency)
              if (addTimelineEventRef.current) {
                addTimelineEventRef.current({
                  type: 'fallecimiento',
                  action: `FALLECIMIENTO: ${response.data.cause_of_death}`,
                  details: {
                    cause_of_death: response.data.cause_of_death,
                    time_in_paro: response.data.time_in_paro,
                    time_in_critical: response.data.time_in_critical
                  }
                });
              }
            }
          }
        }
      } catch (error) {
        console.error('Evolution error:', error);
      }
    };

    // Set up interval to check for evolution every 20 seconds
    evolutionIntervalRef.current = setInterval(evolvePatient, INACTIVITY_THRESHOLD);
    
    return () => {
      if (evolutionIntervalRef.current) {
        clearInterval(evolutionIntervalRef.current);
        evolutionIntervalRef.current = null;
      }
    };
  }, [isRunning, session?.id, elapsedTime, isDeceased]);

  // Calculate patient state based on vitals
  const calculatePatientState = useCallback((v) => {
    if (v.fc === 0 || v.pa_sistolica === 0) return 'paro';
    if (v.fc < 40 || v.fc > 150 || v.spo2 < 80 || v.pa_sistolica < 70) return 'critico';
    if (v.fc < 50 || v.fc > 120 || v.spo2 < 90 || v.pa_sistolica < 90) return 'alerta';
    return 'estable';
  }, []);

  // Apply vital changes - always use the latest values
  const applyVitalChanges = useCallback(async (changes) => {
    // Mark action time immediately to prevent evolution
    lastActionTimeRef.current = Date.now();
    
    // Get base vitals and calculate new values
    const baseVitals = localVitalsRef.current || vitals;
    const newVitals = { ...baseVitals };
    
    Object.entries(changes).forEach(([key, value]) => {
      if (key in newVitals) {
        const maxVal = key === 'fc' ? 200 : key === 'spo2' ? 100 : key === 'temperatura' ? 42 : 250;
        newVitals[key] = Math.max(0, Math.min(maxVal, newVitals[key] + value));
      }
    });
    
    // Update local reference immediately
    localVitalsRef.current = newVitals;
    
    // Update state
    setVitals(newVitals);
    
    // Calculate and update patient state
    const newState = calculatePatientState(newVitals);
    setPatientState(newState);
    
    // Sync with server immediately to ensure persistence
    if (session) {
      try {
        await simulationsAPI.updateState(session.id, {
          vitales: newVitals,
          estado_paciente: newState
        });
      } catch (error) {
        console.error('Error syncing vitals with server:', error);
      }
    }
  }, [calculatePatientState, session, vitals]);

  // Start simulation with role and protocol
  const startSimulation = useCallback(async (scenarioId, options = {}) => {
    try {
      // Reset action time and local vitals reference
      lastActionTimeRef.current = Date.now();
      localVitalsRef.current = null;
      
      // Get role and protocol from options
      const role = options.clinicalRole || clinicalRole || 'medico';
      const protocolId = options.protocolId || null;
      
      const response = await simulationsAPI.start(scenarioId, {
        clinicalRole: role,
        protocolId: protocolId
      });
      const sessionData = response.data;
      setSession(sessionData);
      setClinicalRole(role);
      
      // Get full scenario data
      const simResponse = await simulationsAPI.get(sessionData.id);
      const scenarioData = simResponse.data.scenario;
      setScenario(scenarioData);
      
      // Load available protocols for this scenario
      if (scenarioData?.protocol_ids?.length > 0) {
        try {
          const protocolsRes = await protocolsAPI.getAll({ escenario_id: scenarioId });
          setAvailableProtocols(protocolsRes.data);
          
          // Set selected protocol
          const activeProtocolId = protocolId || scenarioData.default_protocol_id || scenarioData.protocol_ids[0];
          const activeProtocol = protocolsRes.data.find(p => p.id === activeProtocolId);
          setSelectedProtocol(activeProtocol);
          
          // Load protocol-specific medications and actions filtered by role
          if (activeProtocolId) {
            const [medsRes, actionsRes] = await Promise.all([
              protocolsAPI.getMedications(activeProtocolId, role),
              protocolsAPI.getActions(activeProtocolId, role)
            ]);
            setProtocolMedications(medsRes.data);
            setProtocolActions(actionsRes.data);
          }
        } catch (error) {
          console.error('Error loading protocols:', error);
        }
      }
      
      // Set initial vitals
      if (scenarioData?.estado_inicial) {
        const initialVitals = {
          fc: scenarioData.estado_inicial.fc ?? 0,
          spo2: scenarioData.estado_inicial.spo2 ?? 98,
          pa_sistolica: scenarioData.estado_inicial.pa_sistolica ?? 120,
          pa_diastolica: scenarioData.estado_inicial.pa_diastolica ?? 80,
          fr: scenarioData.estado_inicial.fr ?? 16,
          temperatura: scenarioData.estado_inicial.temperatura ?? 36.5,
          ritmo_ecg: scenarioData.estado_inicial.ritmo_ecg ?? null,
          ecg_regular: scenarioData.estado_inicial.ecg_regular ?? true,
          qrs_type: scenarioData.estado_inicial.qrs_type ?? null,
          qrs_duracion_ms: scenarioData.estado_inicial.qrs_duracion_ms ?? null
        };
        setVitals(initialVitals);
        setPatientState(calculatePatientState(initialVitals));
        // Initialize local reference with initial vitals
        localVitalsRef.current = initialVitals;
      }
      
      setElapsedTime(0);
      setIsTimedOut(false);
      setInstructorPaused(false);
      setExtraTime(0);
      setMedications([]);
      setActions([]);
      setDebriefing(null);
      setSimulationPhase('briefing');
      setAnamnesisHistory([]);
      setPhysicalExamResults([]);
      setLabResults([]);
      setImagingResults([]);
      setTimeline([]);
      setTeamNotifications([]);
      
      // Load scenario-specific medications and actions (filtered by role)
      try {
        const [scenarioMedsRes, scenarioActsRes] = await Promise.all([
          simulationsAPI.getAvailableMedications(sessionData.id),
          simulationsAPI.getAvailableActions(sessionData.id)
        ]);
        setScenarioMedications(scenarioMedsRes.data.medicamentos || []);
        setScenarioActions(scenarioActsRes.data.acciones || []);
        setScenarioClinicalConfig({
          usar_desbloqueo_progresivo: scenarioMedsRes.data.usar_desbloqueo_progresivo,
          current_stage_id: scenarioMedsRes.data.current_stage_id
        });
      } catch (error) {
        console.error('Error loading scenario medications/actions:', error);
        // Fallback to global catalog if scenario config not available
        setScenarioMedications([]);
        setScenarioActions([]);
      }
      
      return sessionData;
    } catch (error) {
      console.error('Error starting simulation:', error);
      throw error;
    }
  }, [calculatePatientState, clinicalRole]);

  // Begin active simulation
  const beginSimulation = useCallback(async () => {
    if (!session) return;
    try {
      await simulationsAPI.begin(session.id);
      setIsRunning(true);
      setSimulationPhase('anamnesis');
    } catch (error) {
      console.error('Error beginning simulation:', error);
      throw error;
    }
  }, [session]);

  // Refresh available medications and actions (for stage changes)
  const refreshAvailableItems = useCallback(async () => {
    if (!session) return;
    try {
      const [medsRes, actsRes] = await Promise.all([
        simulationsAPI.getAvailableMedications(session.id),
        simulationsAPI.getAvailableActions(session.id)
      ]);
      setScenarioMedications(medsRes.data.medicamentos || []);
      setScenarioActions(actsRes.data.acciones || []);
      setScenarioClinicalConfig(prev => ({
        ...prev,
        current_stage_id: medsRes.data.current_stage_id
      }));
    } catch (error) {
      console.error('Error refreshing available items:', error);
    }
  }, [session]);

  // Start treatment phase
  const startTreatmentPhase = useCallback(async () => {
    if (!session) return;
    try {
      await simulationsAPI.startTreatment(session.id);
      setSimulationPhase('tratamiento');
    } catch (error) {
      console.error('Error starting treatment:', error);
      throw error;
    }
  }, [session]);

  // Ask patient (anamnesis)
  const askPatient = useCallback(async (question, category = 'general') => {
    if (!session) return;
    try {
      const response = await patientAPI.askAnamnesis(session.id, {
        pregunta: question,
        categoria: category
      });
      
      const record = {
        pregunta: question,
        respuesta: response.data.respuesta,
        categoria: category,
        tiempo: elapsedTime
      };
      setAnamnesisHistory(prev => [...prev, record]);
      
      return response.data;
    } catch (error) {
      console.error('Error asking patient:', error);
      throw error;
    }
  }, [session, elapsedTime]);

  // Perform physical exam
  const performPhysicalExam = useCallback(async (examType, region = null) => {
    if (!session) return;
    try {
      const response = await patientAPI.performPhysicalExam(session.id, {
        tipo: examType,
        region: region
      });
      
      const record = {
        ...response.data,
        tiempo: elapsedTime
      };
      setPhysicalExamResults(prev => [...prev, record]);
      
      return response.data;
    } catch (error) {
      console.error('Error performing physical exam:', error);
      throw error;
    }
  }, [session, elapsedTime]);

  // Add event to timeline
  const addTimelineEvent = useCallback((eventData) => {
    const event = {
      id: Date.now().toString(),
      timestamp: new Date().toISOString(),
      elapsed_seconds: elapsedTime,
      event_type: eventData.type || 'accion_usuario',
      role: eventData.role || clinicalRole,
      actor: eventData.actor || 'usuario',
      action: eventData.action,
      details: eventData.details || {},
      vitals_snapshot: { ...vitals },
      is_correct: eventData.is_correct !== false,
      feedback: eventData.feedback
    };
    setTimeline(prev => [...prev, event]);
    return event;
  }, [elapsedTime, clinicalRole, vitals]);

  // Keep ref updated for use in useEffect
  useEffect(() => {
    addTimelineEventRef.current = addTimelineEvent;
  }, [addTimelineEvent]);

  // Add team notification
  const addTeamNotification = useCallback((notification) => {
    const notif = {
      id: Date.now().toString(),
      timestamp: new Date().toISOString(),
      elapsed_seconds: elapsedTime,
      role: notification.role,
      action: notification.action,
      message: notification.message
    };
    setTeamNotifications(prev => [...prev, notif]);
    
    // Auto-remove after 5 seconds
    setTimeout(() => {
      setTeamNotifications(prev => prev.filter(n => n.id !== notif.id));
    }, 5000);
    
    return notif;
  }, [elapsedTime]);

  // Order lab tests
  const orderLabTests = useCallback(async (tests) => {
    if (!session) return;
    try {
      const response = await patientAPI.orderLabTests(session.id, tests);
      
      setLabResults(prev => [...prev, ...response.data.results]);
      
      return response.data;
    } catch (error) {
      console.error('Error ordering lab tests:', error);
      throw error;
    }
  }, [session]);

  // Order imaging studies
  const orderImaging = useCallback(async (studies) => {
    if (!session) return;
    try {
      const response = await patientAPI.orderImaging(session.id, studies);
      
      setImagingResults(prev => [...prev, ...response.data.results]);
      
      return response.data;
    } catch (error) {
      console.error('Error ordering imaging:', error);
      throw error;
    }
  }, [session]);

  // Administer medication
  const administerMedication = useCallback(async (medicationData) => {
    if (!session) return;
    try {
      // Mark action time to prevent evolution
      lastActionTimeRef.current = Date.now();
      
      // Include elapsed time in the request
      const response = await simulationsAPI.administerMedication(session.id, {
        ...medicationData,
        time_elapsed_seconds: elapsedTime
      });
      
      // === MANEJAR BLOQUEO POR PROTOCOLO ESTRICTO ===
      if (response.data.blocked) {
        // Retornar la respuesta de bloqueo sin modificar el estado
        return {
          blocked: true,
          message: response.data.message,
          paso_esperado: response.data.paso_esperado,
          severidad: response.data.severidad,
          tipo_bloqueo: response.data.tipo_bloqueo
        };
      }
      
      const record = {
        ...medicationData,
        tiempo: elapsedTime
      };
      setMedications(prev => [...prev, record]);
      
      // Add to timeline with adverse event info if present
      const isAdverse = response.data.is_adverse_event;
      addTimelineEvent({
        type: isAdverse ? 'error_clinico' : 'medicamento_administrado',
        action: `${isAdverse ? '⚠️ ' : ''}Administrado: ${medicationData.nombre}`,
        details: { 
          medicamento: medicationData.nombre, 
          dosis: medicationData.dosis,
          via: medicationData.via,
          is_adverse_event: isAdverse,
          adverse_reason: response.data.adverse_event?.reason
        }
      });
      
      // === ACTUALIZACIÓN DINÁMICA DE VITALES ===
      // Si la API devuelve nuevos vitales, actualizar el estado local
      if (response.data.new_vitals) {
        const newVitals = response.data.new_vitals;
        setVitals(newVitals);
        localVitalsRef.current = newVitals;
      } else if (response.data.efectos_vitales) {
        // Fallback: aplicar efectos manualmente si no hay new_vitals
        applyVitalChanges(response.data.efectos_vitales);
      }
      
      // Actualizar estado del paciente si viene en la respuesta
      if (response.data.patient_state) {
        setPatientState(response.data.patient_state);
      }
      
      // === MANEJAR RECUPERACIÓN ===
      if (response.data.is_recovery && response.data.recovery_status) {
        setIsRecovering(true);
        setRecoveryAlert(response.data.recovery_status);
        // Resetear alertas de deterioro
        setDeteriorationAlerts([]);
        setTimeInParo(0);
        setTimeInCritical(0);
        
        // Auto-clear recovery alert después de 5s
        setTimeout(() => {
          setIsRecovering(false);
          setRecoveryAlert(null);
        }, 5000);
      }
      
      // Process real team actions from API (if not adverse event)
      if (response.data.team_actions && response.data.team_actions.length > 0) {
        response.data.team_actions.forEach(teamAction => {
          setTimeout(() => {
            addTeamNotification({
              role: teamAction.role,
              action: teamAction.id,
              message: `${teamAction.role.charAt(0).toUpperCase() + teamAction.role.slice(1)}: ${teamAction.action}`
            });
          }, teamAction.delay_seconds * 1000);
        });
      }
      
      return response.data;
    } catch (error) {
      console.error('Error administering medication:', error);
      throw error;
    }
  }, [session, elapsedTime, applyVitalChanges, addTimelineEvent, addTeamNotification]);

  // Perform action
  const performAction = useCallback(async (actionData) => {
    if (!session) return;
    try {
      // Mark action time to prevent evolution
      lastActionTimeRef.current = Date.now();
      
      // Include elapsed time in the request
      const response = await simulationsAPI.performAction(session.id, {
        ...actionData,
        time_elapsed_seconds: elapsedTime
      });
      
      // === MANEJAR BLOQUEO POR PROTOCOLO ESTRICTO ===
      if (response.data.blocked) {
        // Retornar la respuesta de bloqueo sin modificar el estado
        return {
          blocked: true,
          message: response.data.message,
          paso_esperado: response.data.paso_esperado,
          severidad: response.data.severidad,
          tipo_bloqueo: response.data.tipo_bloqueo
        };
      }
      
      const record = {
        ...actionData,
        tiempo: elapsedTime
      };
      setActions(prev => [...prev, record]);
      
      // Add to timeline with adverse event info if present
      const isAdverse = response.data.is_adverse_event;
      addTimelineEvent({
        type: isAdverse ? 'error_clinico' : 'accion_usuario',
        action: `${isAdverse ? '⚠️ ' : ''}Acción: ${actionData.nombre}`,
        details: { 
          action_id: actionData.action_id,
          nombre: actionData.nombre,
          is_adverse_event: isAdverse,
          adverse_reason: response.data.adverse_event?.reason
        }
      });
      
      // === ACTUALIZACIÓN DINÁMICA DE VITALES ===
      // Si la API devuelve nuevos vitales, actualizar el estado local
      if (response.data.new_vitals) {
        const newVitals = response.data.new_vitals;
        setVitals(newVitals);
        localVitalsRef.current = newVitals;
      } else if (response.data.efectos_vitales) {
        // Fallback: aplicar efectos manualmente si no hay new_vitals
        applyVitalChanges(response.data.efectos_vitales);
      }
      
      // Actualizar estado del paciente si viene en la respuesta
      if (response.data.patient_state) {
        setPatientState(response.data.patient_state);
      }
      
      // === MANEJAR RECUPERACIÓN ===
      if (response.data.is_recovery && response.data.recovery_status) {
        setIsRecovering(true);
        setRecoveryAlert(response.data.recovery_status);
        // Resetear alertas de deterioro
        setDeteriorationAlerts([]);
        setTimeInParo(0);
        setTimeInCritical(0);
        
        // Auto-clear recovery alert después de 5s
        setTimeout(() => {
          setIsRecovering(false);
          setRecoveryAlert(null);
        }, 5000);
      }
      
      // Process real team actions from API (if not adverse event)
      if (response.data.team_actions && response.data.team_actions.length > 0) {
        response.data.team_actions.forEach(teamAction => {
          setTimeout(() => {
            addTeamNotification({
              role: teamAction.role,
              action: teamAction.id,
              message: `${teamAction.role.charAt(0).toUpperCase() + teamAction.role.slice(1)}: ${teamAction.action}`
            });
          }, teamAction.delay_seconds * 1000);
        });
      }
      
      return response.data;
    } catch (error) {
      console.error('Error performing action:', error);
      throw error;
    }
  }, [session, elapsedTime, applyVitalChanges, addTimelineEvent, addTeamNotification]);

  // End simulation
  const endSimulation = useCallback(async () => {
    if (!session) return;
    try {
      setIsRunning(false);
      
      // Update final state
      await simulationsAPI.updateState(session.id, {
        vitales: vitals,
        estado_paciente: patientState,
        tiempo_transcurrido: elapsedTime
      });
      
      await simulationsAPI.end(session.id);
    } catch (error) {
      console.error('Error ending simulation:', error);
      throw error;
    }
  }, [session, vitals, patientState, elapsedTime]);

  // Generate debriefing
  const generateDebriefing = useCallback(async (sessionIdParam) => {
    const sid = sessionIdParam || session?.id;
    if (!sid) return;
    try {
      const response = await simulationsAPI.generateDebriefing(sid);
      setDebriefing(response.data);
      return response.data;
    } catch (error) {
      console.error('Error generating debriefing:', error);
      throw error;
    }
  }, [session]);

  // Fase D: el instructor extiende el tiempo → ampliar límite local
  const extendLocalTimeout = useCallback((seconds) => {
    setExtraTime(prev => prev + seconds);
  }, []);

  // Reset simulation
  const resetSimulation = useCallback(() => {
    setSession(null);
    setScenario(null);
    setIsTimedOut(false);
    setInstructorPaused(false);
    setExtraTime(0);
    setVitals({
      fc: 80,
      spo2: 98,
      pa_sistolica: 120,
      pa_diastolica: 80,
      fr: 16,
      temperatura: 36.5
    });
    setPatientState('estable');
    setElapsedTime(0);
    setMedications([]);
    setActions([]);
    setIsRunning(false);
    setDebriefing(null);
    setSimulationPhase('briefing');
    setAnamnesisHistory([]);
    setPhysicalExamResults([]);
    setLabResults([]);
    setImagingResults([]);
    setClinicalRole('medico');
    setSelectedProtocol(null);
    setAvailableProtocols([]);
    setProtocolMedications([]);
    setProtocolActions([]);
    setTimeline([]);
    setTeamNotifications([]);
  }, []);

  // Format time
  const formatTime = useCallback((seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }, []);

  // Stable callback so consumers (e.g. useStudentSimulationWebSocket) don't
  // re-trigger effects on every provider render. Was previously an inline
  // arrow which caused a WS reconnect storm (1006 loop).
  const markInstructorOverride = useCallback(() => {
    instructorOverrideAtRef.current = Date.now();
  }, []);

  const value = {
    session,
    scenario,
    vitals,
    setVitals,
    activeVitalsTransition,
    setActiveVitalsTransition,
    patientState,
    setPatientState,
    elapsedTime,
    medications,
    actions,
    medicationsCatalog,
    actionsCatalog,
    labTestsCatalog,
    imagingCatalog,
    catalogsLoaded,
    isRunning,
    debriefing,
    simulationPhase,
    setSimulationPhase,
    anamnesisHistory,
    physicalExamResults,
    labResults,
    imagingResults,
    // Clinical role and protocol
    clinicalRole,
    setClinicalRole,
    selectedProtocol,
    setSelectedProtocol,
    availableProtocols,
    protocolMedications,
    protocolActions,
    timeline,
    teamNotifications,
    addTimelineEvent,
    addTeamNotification,
    // Scenario-specific medications and actions (filtered by role)
    scenarioMedications,
    scenarioActions,
    scenarioClinicalConfig,
    useScenarioFiltering,
    setUseScenarioFiltering,
    refreshAvailableItems,
    // Deterioration and death state
    isDeceased,
    causeOfDeath,
    deteriorationAlerts,
    eventAlerts,  // Eventos condicionales del escenario (IAM Demo)
    setEventAlerts,
    timeInParo,
    timeInCritical,
    criticalOmissions,
    // Recovery state
    recoveryAlert,
    isRecovering,
    // Timeout de servidor (Fase C)
    isTimedOut,
    // Fase D: pausa del instructor y extensión de tiempo
    instructorPaused,
    setInstructorPaused,
    extendLocalTimeout,
    markInstructorOverride,
    extraTime,
    // Functions
    startSimulation,
    beginSimulation,
    startTreatmentPhase,
    askPatient,
    performPhysicalExam,
    orderLabTests,
    orderImaging,
    administerMedication,
    performAction,
    endSimulation,
    generateDebriefing,
    resetSimulation,
    applyVitalChanges,
    formatTime,
    audioContextRef,
  };

  return (
    <SimulationContext.Provider value={value}>
      {children}
    </SimulationContext.Provider>
  );
};

export const useSimulation = () => {
  const context = useContext(SimulationContext);
  if (!context) {
    throw new Error('useSimulation must be used within SimulationProvider');
  }
  return context;
};
